import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Grid, RadioGroup } from 'ht-styleguide';

/* Store */
import { useHomeHubStoreAnimationIsFinished, useHomeHubStoreActions } from '@scenes/HomeHub/store/client/homehub.store';

/* Hooks */
import useHomeHubAnswerController from '@scenes/HomeHub/hooks/hook.answerController';

/* Components */
import RadioSelector from '@scenes/HomeHub/components/Selectors/Radio/RadioSelector';

/* Utils/Constants */
import { getGridColumnDetails } from '../utils';
import { sleep } from '@utils/helpers';

import { TQuestionAnswer } from '@scenes/HomeHub/homehub.types';

import styles from '../selectors.module.scss';

const RadioSelectorGroup = () => {
  /* Store */
  const { setSelectedAnswersClientTransient } = useHomeHubStoreActions();

  /* Hooks */
  const { answers, selectedAnswers, gridColumn, answerSchemaNormalizer } = useHomeHubAnswerController();
  const animationIsFinished = useHomeHubStoreAnimationIsFinished();
  /* Extracting selectedAnswer pre-states: Can only ever be a single value */
  const radioValue = selectedAnswers?.id ?? null;
  const radioValueOther = selectedAnswers?.data_value ?? null;

  /* Local State */
  const [value, setValue] = useState<string | number | undefined>(radioValue);
  const findOtherAnswer = answers.find((answer: TQuestionAnswer) => answer.custom_input);
  const isOtherValue = value === findOtherAnswer?.id;
  const showInput = isOtherValue;
  const inputRef = useRef<HTMLInputElement>(null);

  /* -------- Methods -------------- */
  const normalizeData = answerSchemaNormalizer.selectedAnswers({ selectedAnswers }).write;
  const onHandleSelectorChange = useCallback(
    (selectedValue: string | number | undefined) => {
      setValue(selectedValue);
      setSelectedAnswersClientTransient(normalizeData({ id: selectedValue }));
    },
    [normalizeData, setSelectedAnswersClientTransient],
  );

  const onHandleInputChange = useCallback(
    (stringValue: string | number) => {
      const val = stringValue?.toString().trim();
      if (!val) {
        setSelectedAnswersClientTransient([]);
      } else {
        setSelectedAnswersClientTransient(normalizeData({ id: value, custom: val }));
      }
    },
    [normalizeData, value, setSelectedAnswersClientTransient],
  );

  const focusRadioInput = (time = 1000) => {
    sleep(time).then(() => {
      inputRef.current?.focus();
    });
  };

  const onIconClose = () => {
    setValue('');
    onHandleInputChange('');
  };

  /*
    We need to get the "other" value down into it. Ref if we have an other value text
  */
  useEffect(() => {
    if (showInput && inputRef.current && animationIsFinished) {
      inputRef.current.value = radioValueOther;
      onHandleInputChange(radioValueOther);
      focusRadioInput();
    }
  }, [radioValueOther, showInput, isOtherValue, onHandleSelectorChange, animationIsFinished, onHandleInputChange]);

  const { lgColumn, lgColumnOffset, mdColumn, mdColumnOffset } = getGridColumnDetails(gridColumn);

  return (
    <Grid.Fluid classes={styles.gridContainer}>
      <Grid.Row>
        <Grid.Column sm={4} md={mdColumn} mdOffset={mdColumnOffset} lg={lgColumn} lgOffset={lgColumnOffset}>
          <RadioGroup key="answerSelectorGroup" onChange={onHandleSelectorChange} value={value} disableGrouping columnClassName={styles.radioGroup}>
            {answers.map((answer: TQuestionAnswer) => (
              <RadioSelector
                key={answer.value}
                gridColumn={gridColumn}
                value={answer.id}
                label={answer.text}
                onInputChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  const val = evt.target.value;
                  onHandleInputChange(val);
                }}
                onIconClose={onIconClose}
                showInput={showInput}
                inputRef={inputRef}
              />
            ))}
          </RadioGroup>
        </Grid.Column>
      </Grid.Row>
    </Grid.Fluid>
  );
};

export default React.memo(RadioSelectorGroup);

/*
    NOTE: "Other" is not wired up with the selectedAnswers, as there were no questions that required it for checkbox.
           So, we conveyed this in the ticket and verbally with Patrick. It is understood, and if the need arises,
           we'll add it in then.
*/
